import React, { useRef, useState, useEffect } from 'react';
import classnames from 'classnames';
import Opportunity, {
  EmptyStateCard,
  FixingOpportunityProps,
} from '../Opportunity/Opportunity';
import MoreOpportunities from '../Opportunity/MoreOpportunities';
import { Container } from './OpportunitiesCarousel.styles';
import { IconButton } from '../../IconButton';

const MAX_FIXING_OPPORTUNITIES = 3;

type OpportunitiesCarouselProps = {
  fixingOpportunities: FixingOpportunityProps[];
  moreOpportunities?: {
    caption?: string;
    url?: string;
  };
};

export default function OpportunitiesCarousel({
  fixingOpportunities,
  moreOpportunities,
}: OpportunitiesCarouselProps) {
  const carouselRef = useRef<HTMLUListElement>(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

  // Helper to get card size from CSS var or fallback
  const getCardSize = () => {
    if (!carouselRef.current) return 368;
    const style = getComputedStyle(carouselRef.current);
    const val = style.getPropertyValue('--cardSize');
    return val ? parseInt(val) : 368;
  };

  const slicedFixingOpportunities = fixingOpportunities.slice(
    0,
    MAX_FIXING_OPPORTUNITIES,
  );

  // Scroll logic
  const scrollBy = (direction: 'left' | 'right') => {
    const el = carouselRef.current;
    if (!el) return;
    const cardSize = getCardSize();
    el.scrollBy({
      left: direction === 'left' ? -cardSize : cardSize,
      behavior: 'smooth',
    });
  };

  // Update button visibility
  const updateNav = () => {
    if (fixingOpportunities.length <= 2) {
      setAtStart(true);
      setAtEnd(true);
      return;
    }

    const el = carouselRef.current;
    if (!el) return;

    const children = el.children;
    if (!children.length) return;

    const percentageScrolled = el.scrollLeft / (el.scrollWidth - el.clientWidth);

    setAtStart(percentageScrolled <= 0.1);
    setAtEnd(percentageScrolled >= 0.9);
  };

  useEffect(() => {
    const el = carouselRef.current;
    if (!el) return;
    updateNav();
    el.addEventListener('scroll', updateNav);
    return () => el.removeEventListener('scroll', updateNav);
  }, []);

  useEffect(() => {
    updateNav();
    const el = carouselRef.current;
    if (!el) return;
    el.scrollTo({ left: 0, behavior: 'smooth' });
  }, [fixingOpportunities]);

  const noOpportunities = slicedFixingOpportunities.length == 0;

  const classNames = classnames({
    atStart,
    atEnd,
    noOpportunities,
  });

  return (
    <Container className={classNames}>
      {!atStart && (
        <IconButton
          icon="designSystem.chevronLeft"
          outline={true}
          aria-label={'Previous'}
          onClick={() => scrollBy('left')}
        />
      )}
      <ul className="carousel" ref={carouselRef}>
        {slicedFixingOpportunities.map((fixingOpportunity) => (
          <Opportunity
            issue={fixingOpportunity.issue}
            issueUrl={fixingOpportunity.issueUrl}
            issueServiceUrl={fixingOpportunity.issueServiceUrl}
            menuElement={fixingOpportunity.menuElement}
            key={fixingOpportunity.issue.id}
          />
        ))}
        {!noOpportunities && moreOpportunities && (
          <MoreOpportunities
            caption={moreOpportunities.caption}
            url={moreOpportunities.url}
          />
        )}
        {noOpportunities && <EmptyStateCard />}
      </ul>

      {!atEnd && (
        <IconButton
          outline={true}
          icon="designSystem.chevronRight"
          aria-label={'Next'}
          onClick={() => scrollBy('right')}
        />
      )}
    </Container>
  );
}
